import apiClient from "../api/client";
import { account, messages } from "../constants/actionTypes";
import { isCanceledRequest } from "../helpers/isCanceledRequest";

const getInboxBaseUrl = teamId => `/members/${teamId ? "api-v2-team" : "api-v2"}/inbox`;
const getTeamParams = teamId => (teamId ? { user_id: teamId } : {});
const getResponseData = response => response.data?.data || response.data || {};

// The server's snake_case `pagination` block as numbers. A response without one (an older or failed-open
// payload) reads as "one page, nothing more", so nothing offers to load another.
const toPagination = pagination => ({
  page: Number(pagination?.page) || 1,
  perPage: Number(pagination?.per_page) || 0,
  totalPages: Number(pagination?.total_pages) || 0,
  totalRecords: Number(pagination?.total_records) || 0
});

const toListFailure = (error, section) => ({
  section,
  message: error?.response?.data?.error || error?.response?.data?.message || "We couldn't load your messages.",
  status: error?.response?.status || null,
  details: error?.response?.data || null
});

// A row action (read, unread, archive, un-archive) that was refused or never answered. The member is shown a fixed
// string, not `message`: it is kept for the store, as for the list and send failures.
const toActionFailure = (section, error) => ({
  type: messages.MESSAGE_ACTION_FAIL,
  payload: { section, message: error?.response?.data?.error || null, status: error?.response?.status || null }
});

// One list GET at a time, first page or load-more. A slower search response must not replace a newer term, and
// a page requested for the previous list must not land on the next one.
let inboxListController = null;
let inboxListGeneration = 0;

// Counts requests overlap when actions follow one another quickly. A response may set the badge only if no response
// to a newer request has already done so; a newer request that failed does not hold an older success back.
let inboxCountsGeneration = 0;
let inboxCountsAppliedGeneration = 0;

export const actions = {
  /**
   * Loads the inbox list. The server filters archived and search; unread filtering happens here, on the fetched page.
   *
   * @param {Object} [options]
   * @param {string} [options.messageId] The conversation the URL points at. If the list does not hold it, it is
   *   opened through getMessageDetail's personal-inbox fallback (deep links). A load that a change of tab, or Unread
   *   switched on, caused passes none: the page replaces the conversation the new list does not hold instead
   *   (GROWTH-7224).
   * @param {boolean} [options.isArchive] Load the archived tab instead of the inbox tab.
   * @param {Object} [options.team] The team whose inbox to load; the selected team in the store wins.
   * @param {boolean} [options.isUnread] Keep only unread conversations.
   * @param {string} [options.sort] "asc" or "desc"; empty sends "desc", the server's own default.
   * @param {string} [options.search] The applied search term; empty sends none.
   * @returns {Promise<Object[]|null>} The rows it stored in `messages` (only the unread ones for isUnread), or null when
   *   a newer list request outdated it or it failed. It never rejects, so the page can wait on it to settle the open
   *   conversation without a catch of its own (GROWTH-7224).
   */
  getMessages({ messageId, isArchive = false, team = {}, isUnread = false, sort = "", search = "" } = {}) {
    return (dispatch, getState) => {
      const {
        teams: { currentTeam }
      } = getState();
      // Deep-linked messages that aren't in this team's list are handled below (fetched via
      // getMessageDetail's personal-inbox fallback), so the list itself always follows the
      // caller's team selection instead of being forced to personal whenever messageId is set.
      const teamId = currentTeam?.value || team?.value;

      inboxListController?.abort();
      const controller = new AbortController();
      inboxListController = controller;
      const generation = ++inboxListGeneration;

      dispatch(this.toggleLoading("messages", true));

      return apiClient({
        method: "get",
        responseType: "json",
        url: getInboxBaseUrl(teamId),
        params: {
          sort: sort || "desc",
          ...(search ? { search } : {}),
          archived: isArchive ? 1 : 0,
          ...getTeamParams(teamId)
        },
        signal: controller.signal
      })
        .then(result => {
          if (generation !== inboxListGeneration) {
            return null;
          }
          const data = getResponseData(result);
          const { memberships, listing_messages } = data;
          // The server returns exactly one tab's rows; no client-side archive filter.
          const messageRows = listing_messages || [];

          const normalizeListings = memberships
            ? Object.keys(memberships).map(key => ({
                listing_id: key,
                title: memberships[key]
              }))
            : [];

          const unReadMessages = messageRows.filter(message => String(message.is_read) === "0");

          const listRows = isUnread ? unReadMessages : messageRows;

          dispatch({
            type: messages.GET_MESSAGES,
            payload: {
              messages: listRows,
              oldCount: data?.old,
              newCount: data?.new,
              activeListings: normalizeListings,
              pagination: toPagination(data.pagination),
              // The team these rows are for: the page opens nothing from a list that belongs to another one (GROWTH-7324).
              teamId: teamId ? String(teamId) : ""
            }
          });

          if (messageId) {
            const inList = listRows.some(item => String(item.listing_message_id) === String(messageId));
            if (!inList) {
              // A real cross-inbox deep link still opens from the personal inbox. A row the
              // inbox tab filtered out as archived must not: that personal request reopens
              // the thread, and in a team inbox it is the wrong mailbox.
              const openPersonal = () =>
                dispatch(this.getMessageDetail(messageId, null, false, { value: "" }, { suppressArchived: !isArchive }));
              if (!isArchive && teamId) {
                dispatch(
                  this.getMessageDetail(
                    messageId,
                    null,
                    false,
                    { value: teamId },
                    {
                      suppressArchived: true,
                      onMissing: openPersonal
                    }
                  )
                );
              } else {
                openPersonal();
              }
            }
          }

          return listRows;
        })
        .catch(error => {
          if (isCanceledRequest(error) || generation !== inboxListGeneration) {
            return null;
          }
          dispatch({ type: messages.GET_MESSAGES_FAIL, payload: toListFailure(error, "list") });

          return null;
        });
    };
  },

  /**
   * Appends the next page of the list getMessages loaded. Takes the list options the page is showing (not
   * messageId: the open conversation is not this request's business).
   *
   * The page and its size come from the store: `page` is the highest page already loaded and `per_page` is the
   * size the server reported for it, so the client holds no page-size constant. When rows were archived out of
   * the loaded list since the last fetch (`pagination.shifted`), the server's offsets have moved up, so the
   * page already loaded is fetched again (its tail holds the rows that moved) instead of skipping ahead.
   *
   * Does nothing while a list or a page is loading, or once the last page is in.
   *
   * @param {Object} [options] The same shape as getMessages' list options.
   * @returns {Promise<void>} Settles once the page is appended or its failure recorded; already resolved when
   *   nothing is sent. It never rejects.
   */
  loadMoreMessages({ isArchive = false, team = {}, isUnread = false, sort = "", search = "" } = {}) {
    return (dispatch, getState) => {
      const {
        teams: { currentTeam },
        messages: { pagination, preloaders }
      } = getState();

      if (preloaders.messages || preloaders.loadMore || pagination.page >= pagination.totalPages) {
        return Promise.resolve();
      }

      const teamId = currentTeam?.value || team?.value;
      const page = pagination.shifted > 0 ? pagination.page : pagination.page + 1;

      // No abort of the previous request here: the guard above means no list request is in flight. Registering
      // the controller and a new generation is what lets the next getMessages abort and outdate this one.
      const controller = new AbortController();
      inboxListController = controller;
      const generation = ++inboxListGeneration;

      dispatch(this.toggleLoading("loadMore", true));

      return apiClient({
        method: "get",
        responseType: "json",
        url: getInboxBaseUrl(teamId),
        params: {
          sort: sort || "desc",
          ...(search ? { search } : {}),
          archived: isArchive ? 1 : 0,
          page,
          per_page: pagination.perPage,
          ...getTeamParams(teamId)
        },
        signal: controller.signal
      })
        .then(result => {
          if (generation !== inboxListGeneration) {
            return;
          }
          const data = getResponseData(result);
          const messageRows = data.listing_messages || [];
          const pageRows = isUnread ? messageRows.filter(message => String(message.is_read) === "0") : messageRows;

          dispatch({
            type: messages.APPEND_MESSAGES,
            payload: {
              messages: pageRows,
              pagination: toPagination(data.pagination)
            }
          });
        })
        .catch(error => {
          if (isCanceledRequest(error) || generation !== inboxListGeneration) {
            return;
          }
          // Its own section: the page-level retry banner is driven by errors.list, and a failed older page must
          // not cover the list the member is reading.
          dispatch({ type: messages.GET_MESSAGES_FAIL, payload: toListFailure(error, "loadMore") });
        });
    };
  },

  getMessageDetail(listingId, emailConversationId, isNotRead, team, options = {}) {
    return (dispatch, getState) => {
      const {
        teams: { currentTeam }
      } = getState();
      const teamId = team && Object.prototype.hasOwnProperty.call(team, "value") ? team.value : currentTeam?.value;
      const baseUrl = getInboxBaseUrl(teamId);
      const teamParams = getTeamParams(teamId);

      // The requests have no abort, so one can settle after the member opened another conversation (GROWTH-7351).
      // selectedConversationId, written by GET_MESSAGE_DETAIL_LOADING below, is the one the page is opening. The reducer
      // drops a late GET_MESSAGE_DETAIL and GET_MESSAGE_DETAIL_FAIL by itself; this is for what does not go through it.
      const isStillOpening = () => String(getState().messages.selectedConversationId) === String(listingId);

      dispatch({
        type: messages.GET_MESSAGE_DETAIL_LOADING,
        payload: { listingId }
      });

      Promise.all([
        apiClient({
          method: "get",
          responseType: "json",
          url: baseUrl,
          params: {
            listing_message_id: listingId,
            ...teamParams
          }
        }),
        apiClient({
          method: "get",
          responseType: "json",
          url: `${baseUrl}/history`,
          params: {
            listing_message_id: listingId,
            ...teamParams
          }
        })
      ])
        .then(([messageResult, historyResult]) => {
          if (isNotRead && emailConversationId) {
            dispatch(this.readMessage(emailConversationId));
          }

          const messageDetail = getResponseData(messageResult);
          const history = getResponseData(historyResult);

          // Inbox-tab deep link: the detail endpoint still returns an archived thread.
          // Leave the pane clear instead of putting that thread back on screen.
          if (options.suppressArchived && String(messageDetail.is_archive) === "1") {
            // The spinner belongs to the conversation being opened: a newer one still loading owns it now.
            if (isStillOpening()) {
              dispatch(this.toggleLoading("messageDetail", false));
            }
            return;
          }

          dispatch({
            type: messages.GET_MESSAGE_DETAIL,
            payload: { listingId, messageDetail, messages: history.history || [] }
          });
        })
        .catch(error => {
          if (typeof options.onMissing === "function") {
            // The fallback opens the conversation again: not for one the member has left, over the one now open.
            if (isStillOpening()) {
              options.onMissing();
            }
            return;
          }
          // No fallback left to try: report it, with the request itself so the pane's Retry can replay it as it was
          // (a deep-link thread is not in the list, and the team is the one this request used).
          dispatch({
            type: messages.GET_MESSAGE_DETAIL_FAIL,
            payload: {
              listingId,
              message: error?.response?.data?.error || error?.response?.data?.message || "We couldn't load this conversation.",
              status: error?.response?.status || null,
              details: error?.response?.data || null,
              request: { listingId, emailConversationId, isNotRead, team, suppressArchived: Boolean(options.suppressArchived) }
            }
          });
        });
    };
  },

  /**
   * Refreshes the sidebar Inbox badge (`profile.new_messages`) and `profile.unread_messages` from the server's own
   * count (GET /api-v2/inbox/counts, the same numbers the dashboard bootstrap carries). It does not re-issue the
   * bootstrap and it does no arithmetic of its own.
   *
   * Does nothing while a team is selected: the badge is the member's own count and a team's is a different number.
   * A failed or malformed response leaves the badge as it was, with no error shown.
   *
   * @returns {Promise<void>} Settles once the counts are stored or the attempt is dropped. It never rejects.
   */
  getInboxCounts() {
    return (dispatch, getState) => {
      // `teams?.`: the My Feed card (GROWTH-7133) dispatches this on open, and its tests mount the page without that slice.
      const currentTeam = getState().teams?.currentTeam;

      if (currentTeam?.value) {
        return Promise.resolve();
      }

      const generation = ++inboxCountsGeneration;

      return apiClient({
        method: "get",
        responseType: "json",
        url: `${getInboxBaseUrl("")}/counts`
      })
        .then(result => {
          const data = getResponseData(result);
          if (generation < inboxCountsAppliedGeneration || data.new_messages === undefined || data.unread_messages === undefined) {
            return;
          }

          inboxCountsAppliedGeneration = generation;
          dispatch({
            type: account.SET_INBOX_COUNTS,
            payload: { new_messages: String(data.new_messages), unread_messages: String(data.unread_messages) }
          });
        })
        .catch(() => {});
    };
  },

  readMessage(emailConversationId) {
    return (dispatch, getState) => {
      const {
        teams: { currentTeam }
      } = getState();
      const teamId = currentTeam?.value;

      dispatch(this.toggleLoading("readMessage", true));

      apiClient({
        method: "post",
        url: `${getInboxBaseUrl(teamId)}/read`,
        data: teamId ? { ids: [+emailConversationId], user_id: teamId } : [+emailConversationId]
      })
        .then(() => {
          // Clears the unread dot in the list. Dispatched only on success, so a failed POST leaves
          // the row unread rather than claiming a server state that was never written.
          dispatch({ type: messages.READ_MESSAGE, payload: emailConversationId });
          dispatch(this.toggleLoading("readMessage", false));
        })
        .catch(error => {
          dispatch(this.toggleLoading("readMessage", false));
          dispatch(toActionFailure("markRead", error));
        });
    };
  },

  unreadMessage(emailConversationId) {
    return (dispatch, getState) => {
      const {
        teams: { currentTeam }
      } = getState();
      const teamId = currentTeam?.value;

      dispatch(this.toggleLoading("unreadMessage", true));

      apiClient({
        method: "post",
        url: `${getInboxBaseUrl(teamId)}/unread`,
        data: teamId ? { ids: [+emailConversationId], user_id: teamId } : [+emailConversationId]
      })
        .then(() => {
          dispatch({ type: messages.UNREAD_MESSAGE, payload: emailConversationId });
        })
        .catch(error => {
          dispatch(this.toggleLoading("unreadMessage", false));
          dispatch(toActionFailure("markUnread", error));
        });
    };
  },

  /**
   * Archives a conversation.
   *
   * @param {string|number} emailConversationId The list row's email_conversation_id, which the request and the reducer key on.
   * @param {string|number} messageId The open thread's listing_message_id; the reducer clears the open thread by it (GROWTH-6692).
   * @returns {Promise<boolean>} true once the server has confirmed and the row has left the list, false if the request
   *   failed. It never rejects, so the page can wait on it to move the selection without a catch of its own (GROWTH-6714).
   */
  archiveMessage(emailConversationId, messageId) {
    return (dispatch, getState) => {
      const {
        teams: { currentTeam }
      } = getState();
      const teamId = currentTeam?.value;

      dispatch(this.toggleLoading("archiveMessage", true));

      return apiClient({
        method: "post",
        url: `${getInboxBaseUrl(teamId)}/archive`,
        data: teamId ? { ids: [+emailConversationId], user_id: teamId } : [+emailConversationId]
      })
        .then(() => {
          dispatch({
            type: messages.ARCHIVE_MESSAGE,
            payload: { emailConversationId, listingMessageId: messageId }
          });
          dispatch(this.toggleLoading("archiveMessage", false));
          return true;
        })
        .catch(error => {
          dispatch(this.toggleLoading("archiveMessage", false));
          dispatch(toActionFailure("archive", error));
          return false;
        });
    };
  },

  /**
   * Un-archives a conversation. It does not reload the conversation it just took out of the Archived view: the row has left
   * the list, and the page selects its neighbour instead (GROWTH-6714).
   *
   * @param {string|number} emailConversationId The list row's email_conversation_id.
   * @returns {Promise<boolean>} true once the server has confirmed and the row has left the list, false if the request
   *   failed. It never rejects.
   */
  unArchiveMessage(emailConversationId) {
    return (dispatch, getState) => {
      const {
        teams: { currentTeam }
      } = getState();
      const teamId = currentTeam?.value;

      dispatch(this.toggleLoading("unArchiveMessage", true));

      return apiClient({
        method: "post",
        url: `${getInboxBaseUrl(teamId)}/unarchive`,
        data: teamId ? { ids: [+emailConversationId], user_id: teamId } : [+emailConversationId]
      })
        .then(() => {
          dispatch({ type: messages.UN_ARCHIVE_MESSAGE, payload: emailConversationId });
          dispatch(this.toggleLoading("unArchiveMessage", false));
          return true;
        })
        .catch(error => {
          dispatch(this.toggleLoading("unArchiveMessage", false));
          dispatch(toActionFailure("unArchive", error));
          return false;
        });
    };
  },

  sendMessage(messageData) {
    return (dispatch, getState) => {
      const {
        teams: { currentTeam }
      } = getState();

      dispatch(this.toggleLoading("sendMessage", true));
      const teamId = currentTeam?.value;

      apiClient({
        method: "post",
        responseType: "json",
        url: `${getInboxBaseUrl(teamId)}/reply`,
        data: { ...messageData, ...getTeamParams(teamId) }
      })
        .then(result => {
          dispatch({
            type: messages.SEND_MESSAGE,
            payload: {
              message: { ...getResponseData(result).message, lawyer_image: messageData.lawyer_image, is_lawyer: messageData.is_lawyer },
              listing_message_id: messageData.listing_message_id
            }
          });
        })
        .catch(error => {
          dispatch({
            type: messages.SEND_MESSAGE_FAIL,
            payload: {
              section: "sendMessage",
              message: error?.response?.data?.error || error?.response?.data?.message || "Your message wasn't sent.",
              status: error?.response?.status || null,
              details: error?.response?.data || null,
              request: messageData
            }
          });
        });
    };
  },

  clearSendFailure(listingMessageId) {
    return {
      type: messages.CLEAR_SEND_FAILURE,
      payload: listingMessageId
    };
  },

  // Keeps (or, for empty text, drops) the unsent reply typed for one conversation. Dispatched when the reply box stops
  // being that conversation's box (a switch, or going away), never per keystroke.
  setDraft(listingMessageId, text) {
    return {
      type: messages.SET_DRAFT,
      payload: { listingMessageId, text }
    };
  },

  /**
   * Claim a sender DM thread with a signed token from the notification email.
   * Always posts to the personal inbox API (never team).
   * @returns {Promise<{claimed: boolean, listing_message_id: number}>}
   */
  claimThread(token) {
    return () =>
      apiClient({
        method: "post",
        responseType: "json",
        url: "/members/api-v2/inbox/claim",
        data: { token }
      }).then(result => getResponseData(result));
  },

  setActivityFilter(isActive) {
    return {
      type: messages.SET_ACTIVITY_FILTER,
      payload: isActive
    };
  },
  disableSuccess() {
    return {
      type: messages.DISABLE_SUCCESS
    };
  },
  setActiveBlocks(blocks) {
    return {
      type: messages.SET_ACTIVE_BLOCKS,
      payload: blocks
    };
  },

  clearMessageActionErrors() {
    return { type: messages.CLEAR_MESSAGE_ACTION_ERRORS };
  },

  toggleLoading(section, isLoading) {
    return {
      type: messages.TOGGLE_LOADING,
      payload: { section, isLoading }
    };
  }
};
